import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Map from "../../components/Map";
import Header from "../../components/Header";

const TaskDetail = () => {
  const { id } = useParams(); // Get the task id from the URL parameter
  const [users, setUsers] = useState([]);
  const [task, setTask] = useState(null);
  const [taskDetails, setTaskDetails] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [assignedUser, setAssignedUser] = useState([]);
  const [selectedDetailForPhotos, setSelectedDetailForPhotos] = useState(null);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  const fetchTaskDetail = async () => {
    const fetchedLocations = [];
    try {
      // Get the token from localStorage
      const token = localStorage.getItem("token");

      // Send the GET request with the token in the headers
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/task/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`, // Add token to the request headers
          },
        }
      );
      const latLngs = response.data.data.task_details;
      fetchedLocations.push(...latLngs);
      setLocations((prevLocations) => {
        const updatedLocations = [
          ...fetchedLocations.map((item) => ({
            ...item,
            color:
              item.status === 0 || item.status === 1 || item.status === 5
                ? "red"
                : item.status === 2 || item.status === 3
                  ? "blue"
                  : item.status === 4
                    ? "green"
                    : "defaultColor",
          })),
        ];

        return updatedLocations;
      });
      setUsers(response.data.data.user);
      setAssignedUser(response.data.data.user_tasks);
      setTask(response.data.data.task); // Set task data
      setTaskDetails(response.data.data.task_details); // Set task details
    } catch (error) {
      setError("Failed to fetch task details.");
      console.error("Error fetching task details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Assign Location Modal & Selected Officers
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedOfficersForLocation, setSelectedOfficersForLocation] = useState([]);
  const [assigningLocation, setAssigningLocation] = useState(false);

  const handleAssignLocationClick = () => {
    const yellowTaskDetails = locations
      .filter((item) => item.color === "yellow")
      .map((item) => item.id);

    if (yellowTaskDetails.length === 0) {
      alert("Silakan pilih lokasi (klik pin pada peta hingga berwarna kuning) terlebih dahulu!");
      return;
    }

    // Default: select all assigned officers
    setSelectedOfficersForLocation(assignedUser.map((u) => u.id));
    setIsAssignModalOpen(true);
  };

  const handleOfficerCheckboxChange = (officerId) => {
    setSelectedOfficersForLocation((prev) => {
      if (prev.includes(officerId)) {
        return prev.filter((id) => id !== officerId);
      } else {
        return [...prev, officerId];
      }
    });
  };

  const submitAssignLocation = async (usePublicQueue = false) => {
    const yellowTaskDetails = locations
      .filter((item) => item.color === "yellow")
      .map((item) => item.id);

    const officersToAssign = usePublicQueue ? [] : selectedOfficersForLocation;

    if (!usePublicQueue && officersToAssign.length === 0) {
      alert("Pilih minimal satu petugas, atau gunakan opsi 'Antrean Publik'.");
      return;
    }

    setAssigningLocation(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/assign-location`,
        {
          taskDetails: yellowTaskDetails,
          userIds: officersToAssign,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        alert("Lokasi berhasil ditugaskan!");
        setIsAssignModalOpen(false);
        fetchTaskDetail();
      } else {
        throw new Error("Gagal menugaskan lokasi");
      }
    } catch (error) {
      console.error("Error assigning location:", error);
      alert(error.message || "Gagal menugaskan lokasi");
    } finally {
      setAssigningLocation(false);
    }
  };

  const handleAssignTask = async () => {
    if (!selectedUsers.length) {
      alert("Please select at least one user to assign the task.");
      return;
    }

    const ids = selectedUsers;

    try {
      // Get the token from localStorage
      const token = localStorage.getItem("token");

      // Send the POST request with the token in the headers
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/assign-task`,
        { userIds: ids, taskId: task.id },
        {
          headers: {
            Authorization: `Bearer ${token}`, // Add token to the request headers
          },
        }
      );

      fetchTaskDetail();

      if (response.status !== 200) {
        throw new Error(`Task not found`);
      }

      alert("Task registered succesfully!");
    } catch (error) {
      console.error("Error fetching tasks:", error);
      alert(error.message);
    }
  };

  const handleCheckboxChange = (userId) => {
    setSelectedUsers((prevSelected) => {
      if (prevSelected.includes(userId)) {
        // If user is already selected, remove them
        return prevSelected.filter((id) => id !== userId);
      } else {
        // If user is not selected, add them
        return [...prevSelected, userId];
      }
    });
  };

  useEffect(() => {
    fetchTaskDetail();
  }, [id]); // Fetch task detail when the id changes

  const handlePinUpdate = (updatedPins) => {
    setLocations(updatedPins); // Update the locations with the modified pins from the child
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return "Belum Selesai";
      case 1:
        return "Sudah Ditugaskan";
      case 2:
        return "Sudah Ditugaskan ke Petugas";
      case 3:
        return "Sedang Dikerjakan Petugas";
      case 4:
        return "Selesai";
      case 5:
        return "Tugas Dibatalkan";
      default:
        return `Status: ${status}`;
    }
  };

  const getTaskStatusText = (status) => {
    switch (status) {
      case 0:
        return "Dibuat (Draft)";
      case 1:
        return "Ditugaskan";
      case 2:
        return "Dibatalkan";
      case 3:
        return "Lewat Deadline";
      default:
        return `Status: ${status}`;
    }
  };

  const getPetugasName = (assignedAt) => {
    if (!assignedAt) return "-";
    
    // If it's a comma-separated list of IDs (starts and ends with comma, e.g. ",12,15,")
    if (assignedAt.startsWith(",") && assignedAt.endsWith(",")) {
      const ids = assignedAt.split(",").filter(Boolean);
      const names = ids.map(id => {
        const found = users.find(u => u.id.toString() === id.toString()) || 
                      assignedUser.find(u => u.id.toString() === id.toString());
        return found ? (found.name || found.username) : null;
      }).filter(Boolean);
      return names.length > 0 ? `${names.join(", ")} (Tersedia)` : "Tersedia";
    }

    const found = users.find((u) => u.id.toString() === assignedAt.toString());
    if (found) return found.name || found.username;

    const foundAssigned = assignedUser.find((u) => u.id.toString() === assignedAt.toString());
    if (foundAssigned) return foundAssigned.name || foundAssigned.username;

    return `ID Petugas: ${assignedAt}`;
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    const token = localStorage.getItem("token");
    return `${process.env.REACT_APP_API_URL || "http://localhost:3000"}/api/view-photo?path=${encodeURIComponent(photoPath)}&token=${encodeURIComponent(token)}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-gray-600 text-lg">Loading task details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <p className="text-red-600 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <>
      {" "}
      <Header title="Assign Task" />
      <div className="p-6 pt-24">
        <h1 className="text-2xl font-bold mb-6">Detail Tugas Ganti Meter</h1>
        {task && (
          <div className="flex flex-wrap gap-6">
            <div className="w-full sm:w-1/2 lg:w-1/3">
              <h3 className="text-xl font-semibold">Nomor Tugas:</h3>
              <p>{task.task_no}</p>
            </div>
            <div className="w-full sm:w-1/2 lg:w-1/3">
              <h3 className="text-xl font-semibold">Unit:</h3>
              <p>{task.unit}</p>
            </div>
            <div className="w-full sm:w-1/2 lg:w-1/3">
              <h3 className="text-xl font-semibold">Deadline:</h3>
              <p>{task.dl}</p>
            </div>
            <div className="w-full sm:w-1/2 lg:w-1/3">
              <h3 className="text-xl font-semibold">Status:</h3>
              <p>{getTaskStatusText(task.status)}</p>
            </div>
            <div className="w-full sm:w-1/2 lg:w-1/3">
              <h3 className="text-xl font-semibold">Assigned At:</h3>
              {assignedUser.map((user) => (
                <p key={user.id}>{user.name}</p>
              ))}
            </div>
            {assignedUser.length == 0 ? (
              <div className="w-full sm:w-1/2 lg:w-1/3">
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`user-${user.id}`}
                        onChange={() => handleCheckboxChange(user.id)}
                        checked={selectedUsers.includes(user.id)}
                        className="mr-2"
                      />
                      <label
                        htmlFor={`user-${user.id}`}
                        className="text-gray-700"
                      >
                        {user.name}
                      </label>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleAssignTask}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                >
                  Assign Task
                </button>
              </div>
            ) : (
              <></>
            )}
          </div>
        )}
        {assignedUser.length != 0 ? (
          <button
            onClick={handleAssignLocationClick}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition"
          >
            Assign Location
          </button>
        ) : (
          <></>
        )}
        <h2 className="text-xl font-bold mt-6">Detail Tugas</h2>
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full table-auto border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-4 py-2 text-left">
                  IDPEL
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Nama
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Alamat
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Status
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Petugas
                </th>
                {/* <th className="border border-gray-300 px-4 py-2 text-left">
                  Foto (S3 URL)
                </th> */}
              </tr>
            </thead>
            <tbody>
              {taskDetails.map((detail) => (
                <tr key={detail.id} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">
                    <button
                      onClick={() => {
                        setSelectedDetailForPhotos(detail);
                        setActivePhotoIndex(0);
                      }}
                      className="text-indigo-600 hover:text-indigo-850 hover:underline font-semibold flex items-center gap-1.5 focus:outline-none"
                    >
                      <span>{detail.idpel}</span>
                      {(detail.photo1 || detail.photo2 || detail.photo3 || detail.photo4 || detail.photo5) && (
                        <svg className="w-4 h-4 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {detail.nama}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {detail.alamat}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {getStatusText(detail.status)}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {getPetugasName(detail.assigned_at)}
                  </td>
                  {/* <td className="border border-gray-300 px-4 py-2">
                    <div className="flex flex-col gap-1 text-xs">
                      {detail.photo1 && (
                        <div>
                          <span className="font-semibold">Photo 1:</span>{" "}
                          <a
                            href={`${process.env.REACT_APP_API_URL || "http://localhost:3000"}/images/${detail.photo1.split("/").pop()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline break-all"
                          >
                            {detail.photo1}
                          </a>
                        </div>
                      )}
                      {detail.photo2 && (
                        <div>
                          <span className="font-semibold">Photo 2:</span>{" "}
                          <a
                            href={`${process.env.REACT_APP_API_URL || "http://localhost:3000"}/images/${detail.photo2.split("/").pop()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline break-all"
                          >
                            {detail.photo2}
                          </a>
                        </div>
                      )}
                      {detail.photo3 && (
                        <div>
                          <span className="font-semibold">Photo 3:</span>{" "}
                          <a
                            href={`${process.env.REACT_APP_API_URL || "http://localhost:3000"}/images/${detail.photo3.split("/").pop()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline break-all"
                          >
                            {detail.photo3}
                          </a>
                        </div>
                      )}
                      {detail.photo4 && (
                        <div>
                          <span className="font-semibold">Photo 4:</span>{" "}
                          <a
                            href={`${process.env.REACT_APP_API_URL || "http://localhost:3000"}/images/${detail.photo4.split("/").pop()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline break-all"
                          >
                            {detail.photo4}
                          </a>
                        </div>
                      )}
                      {detail.photo5 && (
                        <div>
                          <span className="font-semibold">Photo 5:</span>{" "}
                          <a
                            href={`${process.env.REACT_APP_API_URL || "http://localhost:3000"}/images/${detail.photo5.split("/").pop()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline break-all"
                          >
                            {detail.photo5}
                          </a>
                        </div>
                      )}
                      {!detail.photo1 && !detail.photo2 && !detail.photo3 && !detail.photo4 && !detail.photo5 && (
                        <span className="text-gray-400">Belum ada foto</span>
                      )}
                    </div>
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Map initialPins={locations} handlePinUpdate={handlePinUpdate}></Map>{" "}
        {/* Pass the callback */}

        {/* Modal: Tugaskan Lokasi ke Petugas */}
        {isAssignModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md border border-slate-200 shadow-xl space-y-4 m-4">
              <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                <h3 className="text-lg font-bold text-slate-950">Tugaskan Lokasi ke Petugas</h3>
                <button
                  onClick={() => setIsAssignModalOpen(false)}
                  className="text-slate-400 hover:text-slate-600 transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <p className="text-sm text-slate-500 font-medium leading-relaxed">
                Pilih petugas untuk mengerjakan <strong className="text-indigo-600 font-bold">{locations.filter(item => item.color === "yellow").length} lokasi</strong> yang Anda pilih di peta. Jika Anda memilih lebih dari satu petugas, lokasi akan dibagi secara merata.
              </p>

              <div className="space-y-2 max-h-48 overflow-y-auto p-1">
                {assignedUser.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100/70 transition"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOfficersForLocation.includes(user.id)}
                      onChange={() => handleOfficerCheckboxChange(user.id)}
                      className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                    />
                    <div className="text-sm font-bold text-slate-800">{user.name}</div>
                  </label>
                ))}
              </div>

              <div className="flex flex-col gap-2 pt-3 border-t border-slate-100">
                <button
                  onClick={() => submitAssignLocation(false)}
                  disabled={assigningLocation || selectedOfficersForLocation.length === 0}
                  className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold text-sm rounded-xl transition shadow-md hover:shadow-indigo-600/10 flex items-center justify-center gap-1.5"
                >
                  {assigningLocation ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    "Tugaskan ke Petugas Terpilih"
                  )}
                </button>
                
                <button
                  onClick={() => submitAssignLocation(true)}
                  disabled={assigningLocation}
                  className="w-full py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-xl transition flex items-center justify-center"
                >
                  Tugaskan ke Antrean Publik
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: View Uploaded Photos */}
        {selectedDetailForPhotos && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl p-6 w-full max-w-4xl border border-slate-200 shadow-2xl flex flex-col md:flex-row gap-6 m-4 max-h-[90vh] overflow-y-auto">
              
              {/* Left Column: Photo Gallery Viewer */}
              <div className="flex-1 flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-slate-950">Foto Bukti Ganti Meter</h3>
                      <p className="text-sm text-slate-500">IDPEL: <strong className="text-indigo-600 font-semibold">{selectedDetailForPhotos.idpel}</strong></p>
                    </div>
                    <button
                      onClick={() => setSelectedDetailForPhotos(null)}
                      className="text-slate-400 hover:text-slate-600 transition md:hidden"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Large Active Photo Preview */}
                  <div className="relative bg-slate-100 rounded-xl overflow-hidden aspect-video flex items-center justify-center border border-slate-200">
                    {(() => {
                      const activePhotoPath = selectedDetailForPhotos[`photo${activePhotoIndex + 1}`];
                      const activePhotoUrl = getPhotoUrl(activePhotoPath);
                      
                      if (activePhotoUrl) {
                        return (
                          <img
                            src={activePhotoUrl}
                            alt={`Foto ${activePhotoIndex + 1}`}
                            className="max-h-full max-w-full object-contain"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.0.3"; // Fallback image
                            }}
                          />
                        );
                      } else {
                        return (
                          <div className="flex flex-col items-center justify-center p-6 text-center">
                            <svg className="w-12 h-12 text-slate-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <p className="text-sm font-semibold text-slate-500">Foto {activePhotoIndex + 1} tidak tersedia</p>
                            <p className="text-xs text-slate-400">Petugas tidak mengunggah berkas untuk slot ini.</p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>

                {/* Thumbnails list */}
                <div className="mt-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Pilih Foto (1-5):</p>
                  <div className="grid grid-cols-5 gap-2">
                    {[1, 2, 3, 4, 5].map((num) => {
                      const photoPath = selectedDetailForPhotos[`photo${num}`];
                      const photoUrl = getPhotoUrl(photoPath);
                      const isActive = activePhotoIndex === num - 1;
                      
                      return (
                        <button
                          key={num}
                          onClick={() => setActivePhotoIndex(num - 1)}
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 transition ${
                            isActive
                              ? "border-indigo-600 ring-2 ring-indigo-600/20"
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          {photoUrl ? (
                            <img
                              src={photoUrl}
                              alt={`Thumb ${num}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-full h-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-400">
                              F{num}
                            </div>
                          )}
                          <div className={`absolute bottom-0 inset-x-0 text-[10px] text-center font-bold py-0.5 leading-none ${isActive ? "bg-indigo-600 text-white" : "bg-black/40 text-white"}`}>
                            Foto {num}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Right Column: Customer Info & Metadata */}
              <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-slate-150 pt-4 md:pt-0 md:pl-6 flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="hidden md:flex justify-between items-start">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Detail Informasi</h4>
                    <button
                      onClick={() => setSelectedDetailForPhotos(null)}
                      className="text-slate-400 hover:text-slate-600 transition"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <span className="text-xs font-semibold text-slate-400">Nama Pelanggan</span>
                      <p className="text-sm font-bold text-slate-800">{selectedDetailForPhotos.nama}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-400">Alamat</span>
                      <p className="text-sm text-slate-600 leading-normal">{selectedDetailForPhotos.alamat}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-400">Nomor KWH Baru</span>
                      <p className="text-sm font-bold text-slate-800">{selectedDetailForPhotos.nomorkwh || "-"}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-400">KWH Cabut</span>
                      <p className="text-sm font-bold text-slate-800">{selectedDetailForPhotos.cabut || "-"}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-400">Status Tugas</span>
                      <div>
                        <span className={`inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-xs font-bold ${
                          selectedDetailForPhotos.status === 4 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
                        }`}>
                          {getStatusText(selectedDetailForPhotos.status)}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-slate-400">Petugas Pelaksana</span>
                      <p className="text-sm font-semibold text-slate-700">{getPetugasName(selectedDetailForPhotos.assigned_at)}</p>
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t border-slate-100 mt-6 flex flex-col gap-2">
                  {(() => {
                    const activePhotoPath = selectedDetailForPhotos[`photo${activePhotoIndex + 1}`];
                    const activePhotoUrl = getPhotoUrl(activePhotoPath);
                    return activePhotoUrl ? (
                      <a
                        href={activePhotoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm rounded-xl transition flex items-center justify-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                        Buka Foto Asli
                      </a>
                    ) : null;
                  })()}
                  <button
                    onClick={() => setSelectedDetailForPhotos(null)}
                    className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl transition shadow-md flex items-center justify-center"
                  >
                    Tutup
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TaskDetail;
